import { Button, Divider, Drawer, Flex, Text, useMantineTheme } from '@mantine/core';
import { NumberInput, Select } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { formatPaymentAmount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import { useMutation } from '@tanstack/react-query';
import {
	addInvestmentLoan,
	removeInvestmentLoan,
	updateInvestmentLoan,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';

import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useGetLoans } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/hooks/useGetLoans.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { CurrencyType, InvestmentDetailsLoan } from '@dealstack/shared/lib/generated';
import { ManageLoanDrawerStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/ManageLoanDrawer/ManageLoanDrawer.styles.tsx';
import { CURRENCY_SYMBOLS } from '@dealstack/shared/lib/format/currency/currency.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import {
	MIP_MANAGE_ROUTES,
	PROGRAMME_SETTINGS_ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { ManageLoanFormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/ManageLoanDrawer/ManageLoanDrawer.types.ts';
import { TwoSideToggle } from '@dealstack/frontend/shared/components/TwoSideToggle/TwoSideToggle.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	loan: InvestmentDetailsLoan | null;
	allLoans: InvestmentDetailsLoan[];
	currency: CurrencyType;
	totalCost: number;
	loanAmount: number;
}

const buildSchema = ({ maxAmount }: { maxAmount: number }) =>
	z
		.object({
			loanDefinitionId: z.string(),
			amount: z.number({ invalid_type_error: 'Amount is required' }),
			includeLoan: z.boolean(),
		})
		.superRefine((data, ctx) => {
			if (!data.includeLoan) {
				return;
			}

			if (!data.loanDefinitionId || data.loanDefinitionId.trim().length === 0) {
				ctx.addIssue({
					path: ['loanDefinitionId'],
					code: z.ZodIssueCode.custom,
					message: 'Select a loan definition',
				});
			}

			if (data.amount <= 0) {
				ctx.addIssue({
					path: ['amount'],
					code: z.ZodIssueCode.custom,
					message: 'Amount must be greater than zero',
				});
			}

			if (maxAmount !== 0 && data.amount > maxAmount) {
				ctx.addIssue({
					path: ['amount'],
					code: z.ZodIssueCode.custom,
					message: `Cannot exceed ${maxAmount}`,
				});
			}
		});

export const ManageLoanDrawer = ({
	opened,
	onClose,
	loan,
	allLoans,
	currency,
	totalCost,
	loanAmount,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const { data: loanDefinitionsResponse } = useGetLoans();
	const { refetch: refetchSummary } = useInvestmentDetailsSummary();
	const mantineTheme = useMantineTheme();

	const addLoanRequest = useMutation({ mutationFn: addInvestmentLoan });
	const updateLoanRequest = useMutation({ mutationFn: updateInvestmentLoan });
	const removeLoanRequest = useMutation({ mutationFn: removeInvestmentLoan });

	const loanDefinitionOptions = useMemo(() => {
		const definitionsUsedByOtherLoans = new Set(
			allLoans.filter((l) => l.id !== loan?.id).map((l) => l.loanDefinitionId)
		);
		return (loanDefinitionsResponse?.payload ?? [])
			.filter((definition) => !definitionsUsedByOtherLoans.has(definition.id))
			.map((definition) => ({
				value: definition.id,
				label: `${definition.name} — ${definition.funder}`,
			}));
	}, [loanDefinitionsResponse, allLoans, loan?.id]);

	const otherLoansTotal = loan ? loanAmount - loan.amount : loanAmount;
	const maxAmount = Math.max(0, totalCost - otherLoansTotal);

	const schema = useMemo(() => buildSchema({ maxAmount }), [maxAmount]);

	const form = useForm<ManageLoanFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			loanDefinitionId: loan?.loanDefinitionId ?? '',
			amount: loan?.amount ?? 0,
			includeLoan: !!loan,
		},
	});

	const loanToggled = form.watch('includeLoan');
	const loanInputAmount = form.watch('amount');
	const totalCostMinusLoans = totalCost - otherLoansTotal - loanInputAmount;
	const remainingCashBalance = totalCostMinusLoans >= 0 ? totalCostMinusLoans : 0;

	useEffect(() => {
		if (opened) {
			form.reset({
				loanDefinitionId: loan?.loanDefinitionId ?? '',
				amount: loan?.amount ?? 0,
				includeLoan: !!loan,
			});
		}
	}, [opened, loan, form]);

	const onSubmit = (values: ManageLoanFormValues) => {
		const handleSuccess = () => {
			void refetchSummary();
			showToastV2({
				title: tMipManage('investment_payment_card.manage_loan.save_success'),
				state: 'success',
			});
			onClose();
		};

		if (!loan) {
			if (!values.includeLoan) {
				onClose();
				return;
			}

			addLoanRequest.mutate(
				{
					mipId,
					investmentId,
					payload: {
						loanDefinitionId: values.loanDefinitionId,
						amount: values.amount,
					},
				},
				{ onSuccess: handleSuccess }
			);
			return;
		}

		if (!values.includeLoan) {
			removeLoanRequest.mutate(
				{
					mipId,
					investmentId,
					loanId: loan.id,
				},
				{ onSuccess: handleSuccess }
			);
			return;
		}

		updateLoanRequest.mutate(
			{
				mipId,
				investmentId,
				loanId: loan.id,
				payload: {
					loanDefinitionId: values.loanDefinitionId,
					amount: values.amount,
				},
			},
			{ onSuccess: handleSuccess }
		);
	};

	const toggleLoan = (toggled: boolean) => {
		form.setValue('includeLoan', toggled);

		if (!toggled) {
			// Clear loan fields + any pending validation errors so toggling
			// back off doesn't leave the form in a stale invalid state.
			form.setValue('loanDefinitionId', '');
			form.setValue('amount', 0);
			form.clearErrors(['loanDefinitionId', 'amount']);
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="484px"
			title={tMipManage('investment_payment_card.manage_loan.title')}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
		>
			<ManageLoanDrawerStyles>
				<FormProvider {...form}>
					<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
						<Flex direction="column" h="100%">
							<div className="prices">
								<Flex direction="column" justify="space-between" align="center">
									<Text c="dark.0" mb="0.5rem">
										{tMipManage(
											'investment_payment_card.manage_loan.total_consideration'
										)}
									</Text>

									<Text fz="lg" fw={600} c="dark.8">
										{formatPaymentAmount(totalCost, currency, 'symbol')}
									</Text>
								</Flex>
							</div>

							<Text fz="lg" fw={600} c="dark.8" mb="0.5rem">
								{tMipManage(
									'investment_payment_card.manage_loan.funding_structure'
								)}
							</Text>

							<div className="structure">
								<div className="structure-head">
									<TwoSideToggle
										leftLabel={tMipManage(
											'investment_payment.manage_loan.toggle_cash_label'
										)}
										rightLabel={tMipManage(
											'investment_payment.manage_loan.toggle_loan'
										)}
										toggled={loanToggled}
										onToggle={toggleLoan}
									/>
								</div>

								<div className="structure-body">
									<Select
										name="loanDefinitionId"
										label={tMipManage(
											'investment_payment_card.manage_loan.definition_label'
										)}
										placeholder={tMipManage(
											'investment_payment_card.manage_loan.definition_placeholder'
										)}
										data={loanDefinitionOptions}
										searchable
										size="md"
										disabled={!loanToggled}
										styles={{
											root: {
												opacity: loanToggled ? 1 : 0.5,
											},
										}}
									/>

									<NumberInput
										name="amount"
										label={tMipManage(
											'investment_payment_card.manage_loan.amount_label',
											{
												currency: CURRENCY_SYMBOLS[currency],
											}
										)}
										placeholder="0.00"
										decimalScale={2}
										min={0}
										size="md"
										hideControls
										mt="1rem"
										disabled={!loanToggled}
										styles={{
											root: {
												opacity: loanToggled ? 1 : 0.5,
											},
										}}
									/>

									<Divider m="1rem 0" />

									<div className="cash-balance">
										<Flex justify="space-between" align="center" gap="1rem">
											<Flex direction="column" gap="0.25rem">
												<Text fw={600} c="#fff">
													{tMipManage(
														'investment_payment_card.manage_loan.remaining_cash_balance'
													)}
												</Text>

												<Text c="#fff">
													{tMipManage(
														'investment_payment_card.manage_loan.required_from_investor'
													)}
												</Text>
											</Flex>

											<Text fz="lg" fw={600} c="#fff">
												{formatPaymentAmount(
													remainingCashBalance,
													currency,
													'symbol'
												)}
											</Text>
										</Flex>
									</div>
								</div>
							</div>

							<Flex justify="flex-start" mt="1rem">
								<Button
									fw={600}
									variant="subtle"
									onClick={() => {
										navigate({
											pathname: mipManagePath(
												`${MIP_MANAGE_ROUTES.SETTINGS}${PROGRAMME_SETTINGS_ROUTES.LOANS}`,
												{
													dealId,
													mipId,
												}
											),
										});
									}}
								>
									<Text mr="1rem" c="blue.6">
										{tMipManage('manage_loan_drawer.loan_settings')}
									</Text>

									<Icon
										variant="arrow-up-right-2"
										size="md"
										color={mantineTheme.colors.blue[6]}
										fillStroke={true}
									/>
								</Button>
							</Flex>

							<Text c="dark.9" mt="0.25rem">
								{tMipManage('manage_loan_drawer.more_settings')}
							</Text>

							<Flex direction="column" mt="auto" gap="1rem">
								<Button
									type="submit"
									fw={600}
									fullWidth
									loading={
										addLoanRequest.isPending ||
										updateLoanRequest.isPending ||
										removeLoanRequest.isPending
									}
								>
									{tMipManage('investment_payment_card.manage_loan.save_cta')}
								</Button>

								<Button
									variant="subtle"
									c="dark.8"
									fullWidth
									loading={
										addLoanRequest.isPending ||
										updateLoanRequest.isPending ||
										removeLoanRequest.isPending
									}
									onClick={onClose}
								>
									{tMipManage('investment_payment_card.manage_loan.cancel_cta')}
								</Button>
							</Flex>
						</Flex>
					</form>
				</FormProvider>
			</ManageLoanDrawerStyles>
		</Drawer>
	);
};
